import React, { useMemo } from 'react';
import styled from 'styled-components';
import { colors, borderRadius, typography, spacing, shadows } from '../../theme/colors';
import { formatCurrency } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';

const CATEGORY_COLORS = [
  '#800020', '#2196F3', '#4CAF50', '#FF9800', '#9C27B0',
  '#00BCD4', '#795548', '#607D8B', '#E91E63', '#3F51B5',
  '#009688', '#FF5722', '#673AB7', '#CDDC39'
];

const POSProductGrid = ({
  items = [],
  categories = [],
  selectedCategoryId,
  onCategorySelect,
  onItemAdd,
  stockBalances = {},
  searchQuery = ''
}) => {
  const { formatCurrency } = useCurrency();

  // Category-first grid: there is no "All" tab, so browsing shows ONE category.
  // A search/scan, however, must reach EVERY item — a barcode belonging to
  // another category has to match or the cashier simply cannot sell it. So when
  // a query is present the category filter is bypassed entirely.
  const filteredItems = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();

    if (q) {
      return items.filter(item =>
        (item.itemName || '').toLowerCase().includes(q) ||
        (item.itemCode || '').toLowerCase().includes(q) ||
        (item.barcode || '').toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q)
      );
    }

    // No query → only the selected category. A company with NO categories has
    // nothing to select, so fall back to the full list instead of a blank grid.
    if (!selectedCategoryId) return items;
    return items.filter(item => item.categoryId === selectedCategoryId);
  }, [items, selectedCategoryId, searchQuery]);

  return (
    <Container>
      {/* Category Row */}
      <CategoryRow>
        {categories.map((cat, idx) => (
          <CategoryButton
            key={cat.id}
            $active={selectedCategoryId === cat.id}
            $color={CATEGORY_COLORS[idx % CATEGORY_COLORS.length]}
            onClick={() => onCategorySelect(cat.id)}
          >
            {cat.categoryName || cat.name}
          </CategoryButton>
        ))}
      </CategoryRow>

      {/* Product Grid */}
      <GridContainer>
        {filteredItems.length === 0 ? (
          <EmptyState>
            {searchQuery ? 'No items match your search' : 'No items in this category'}
          </EmptyState>
        ) : (
          <Grid>
            {filteredItems.map((item, idx) => {
              const stock = stockBalances[item.id] ?? null;
              const lowStock = stock !== null && stock > 0 && stock <= (item.reorderLevel || 5);
              const outOfStock = stock !== null && stock <= 0;
              const hasImage = !!item.imageUrl;
              // Deterministic placeholder color so the same item always gets
              // the same tint — quick visual recognition without an image.
              const placeholderColor = CATEGORY_COLORS[idx % CATEGORY_COLORS.length];

              return (
                <ProductTile
                  key={item.id}
                  onClick={() => onItemAdd(item)}
                  $outOfStock={outOfStock}
                  title={item.itemName}
                >
                  <ImageWrap $bg={hasImage ? null : placeholderColor}>
                    {hasImage ? (
                      <ProductImage src={item.imageUrl} alt={item.itemName} loading="lazy" />
                    ) : (
                      <PlaceholderInitial>
                        {(item.itemName || '?').charAt(0).toUpperCase()}
                      </PlaceholderInitial>
                    )}
                    {stock !== null && (
                      <StockBadge $low={lowStock} $out={outOfStock}>
                        {outOfStock ? 'OUT' : stock}
                      </StockBadge>
                    )}
                  </ImageWrap>
                  <TileBody>
                    <TileName>{item.itemName}</TileName>
                    {item.itemCode && <TileCode>{item.itemCode}</TileCode>}
                    <TilePrice>{formatCurrency(item.salePrice || 0)}</TilePrice>
                  </TileBody>
                </ProductTile>
              );
            })}
          </Grid>
        )}
      </GridContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 0%;
  min-height: 0;
  overflow: hidden;
`;

const CategoryRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  flex: 0 0 auto;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral[300]};
    border-radius: 4px;
  }
`;

const CategoryButton = styled.button`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
  background: ${({ $active, $color }) => $active ? $color : colors.neutral[100]};
  color: ${({ $active }) => $active ? 'white' : colors.text.primary};

  &:hover {
    background: ${({ $color }) => $color};
    color: white;
    opacity: 0.9;
  }
`;

const GridContainer = styled.div`
  flex: 1 1 0%;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral[300]};
    border-radius: 4px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(105px, 1fr));
  gap: 6px;
  padding: 2px;
`;

const ProductTile = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid ${({ $outOfStock }) => $outOfStock ? colors.error.main : colors.border.light};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  overflow: hidden;
  transition: all 0.15s ease;
  opacity: ${({ $outOfStock }) => $outOfStock ? 0.6 : 1};

  &:hover {
    border-color: ${colors.primary.main};
    box-shadow: ${shadows.md};
    transform: translateY(-2px);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const ImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: ${({ $bg }) => $bg || colors.neutral[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PlaceholderInitial = styled.div`
  font-size: 28px;
  font-weight: ${typography.fontWeight.bold};
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  user-select: none;
`;

const StockBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 22px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  color: white;
  background: ${({ $out, $low }) =>
    $out ? colors.error.main :
    $low ? colors.warning.main :
    'rgba(0, 0, 0, 0.55)'};
  backdrop-filter: blur(2px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  pointer-events: none;
`;

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px 7px 7px;
  gap: 1px;
`;

const TileName = styled.div`
  font-size: 11px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.4em;
`;

const TileCode = styled.div`
  font-size: 9px;
  color: ${colors.text.tertiary};
  font-family: monospace;
  letter-spacing: 0.2px;
`;

const TilePrice = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.primary.main};
  margin-top: 1px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.sm};
`;

export default POSProductGrid;
